const clientId = process.env.NEXT_PUBLIC_CLIENT_ID!;
const tokenBaseUrl = process.env.NEXT_PUBLIC_TOKEN_URL!;
const authorizeBaseUrl = process.env.NEXT_PUBLIC_AUTHORIZE_URL!; 
const endSessionUrl = process.env.NEXT_PUBLIC_END_SESSION_URL!;
const responseType = process.env.NEXT_PUBLIC_RESPONSE_TYPE!;
const scopes = process.env.NEXT_PUBLIC_SCOPES!;
const apiUrl = process.env.NEXT_PUBLIC_API_URL!;

export const handleLogin = async () => {
    window.location.href = await getAuthorizeUrl();
};

export const handleLogout = () => {
    const idToken = localStorage.getItem("idToken");
    const postLogoutRedirectUri = encodeURIComponent(`${window.location.origin}/logout/callback`);

    const logOutUrl = `${endSessionUrl}?id_token_hint=${idToken}&post_logout_redirect_uri=${postLogoutRedirectUri}`;
    window.location.href = logOutUrl;
};

const getAuthorizeUrl = async () =>
    {            
        const redirectUri = `${window.location.origin}/callback`;

        const state = crypto.randomUUID();
        const nonce = crypto.randomUUID();

        const codeVerifier = generateRandomString(128);
        const codeChallenge = await generateCodeChallenge(codeVerifier);

        localStorage.setItem("codeVerifier", codeVerifier);
        localStorage.setItem("oauthState", state);

        const params = new URLSearchParams({
            response_type: responseType,
            client_id: clientId, 
            redirect_uri: redirectUri,
            scope: scopes,
            code_Challenge: codeChallenge,
            code_Challenge_Method: "S256", 
            nonce: nonce,
            state: state
        });

        return `${authorizeBaseUrl}?${params.toString()}`;
    };

const generateRandomString = (length: number) => {
    const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    let randomString = "";
    for (let i = 0; i < length; i++) {
        randomString += chars.charAt(
            Math.floor(Math.random() * chars.length)
        );            
    }
    return randomString;
};

const generateCodeChallenge = async (verifier: string) => {
    const encoder = new TextEncoder();
    const data = encoder.encode(verifier);
    const digest = await crypto.subtle.digest("SHA-256", data);
    return btoa(String.fromCharCode(...new Uint8Array(digest)))
        .replace(/\+/g, "-")
        .replace(/\//g, "_")
        .replace(/=+$/, "");
};

export const exchangeCodeForToken = async (code: string): Promise<boolean> => {
    
    const codeVerifier = localStorage.getItem("codeVerifier");

    try {
        const response = await fetch(tokenBaseUrl, {
            method: "POST",
            headers: { "Content-Type": "application/x-www-form-urlencoded" },
            body: new URLSearchParams({
              client_id: clientId,
              code: code,
              redirect_uri: `${window.location.origin}/callback`,
              grant_type: "authorization_code",
              code_verifier: codeVerifier || "",
            }),
          });

          const data = await response.json();
          if(response.ok) {
            const { id_token, access_token, refresh_token, expires_in } = data;
            if (access_token && id_token) {

                const expiryTime = Date.now() + parseInt(expires_in, 10) * 1000;
                
                localStorage.setItem("idToken", id_token);
                localStorage.setItem("authToken", access_token);
                localStorage.setItem("tokenExpiry", expiryTime.toString());
                if (refresh_token) {
                  localStorage.setItem("refreshToken", refresh_token);  
                }
    
                // Notify context to re-read token
                window.dispatchEvent(new Event("storage"));
                return true;
            } else {
                console.error("Token exchange failed: ", data.error);
                return false;
            }
          }
    } catch (error) {
        console.error("Error during token exchange: ", error);
        return false;
    }
    return false;
};

export const refreshAccessToken = async (): Promise<boolean> => {
    const refreshToken = localStorage.getItem("refreshToken");

    if (!refreshToken){
        console.warn("No refresh token available.");
        return false;
    }

    const body = new URLSearchParams({
        client_id: clientId, 
        grant_type: "refresh_token",
        refresh_token: refreshToken
    });

    try {
        const response = await fetch(tokenBaseUrl, {
            method: "POST", 
            headers: { "Content-Type": "application/x-www-form-urlencoded" },
            body: body.toString()
        });
        
        const data = await response.json();

        if(response.ok) {
            const { access_token, refresh_token , expires_in } = data;

            const newExpiryTime = Date.now() + expires_in * 1000;
            localStorage.setItem("authToken", access_token);
            localStorage.setItem("tokenExpiry", newExpiryTime.toString());

            if (refresh_token) {
                localStorage.setItem("refreshToken", refresh_token);
            }

            window.dispatchEvent(new Event("storage"));
            return true;
        } else {
            localStorage.removeItem("authToken");
            localStorage.removeItem("refreshToken");
            localStorage.removeItem("tokenExpiry");

            console.error("Refresh token failed: ", data.error);
            return false;
        }
    } catch (error) {
        console.error("Error during token refresh: ", error);
        return false;
    }
};

export const checkUserApiKeyManager = async (): Promise<boolean> => {
    const response = await fetch(`${apiUrl}/externalauthservice/apikey-authorize-by-role`, {
        method: "GET",
        headers: {
            "Content-Type": "application/x-www-form-urlencoded",
            "Authorization": `Bearer ${localStorage.getItem("authToken")}`
        },
    });
    if (response.ok) {
        localStorage.setItem("isUserApiKeyManager", "true");
        return true;
    } else {
        localStorage.setItem("isUserApiKeyManager", "false");
        return false;
    }
}